import {
  Banner,
  Button,
  EmptyState,
  FlexBox,
  FormField,
  FormGrid,
  Icon,
  Page,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import Fuse from 'fuse.js';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { CreateWhatsAppSignupApiArg, messagingApi } from 'apis/messaging';
import { countries } from 'app/utils/countries/countries';
import { isNumberValidWithReason } from 'app/utils/telephonyHelpers';
import { AppLink } from 'components/appLink';
import {
  HookFormQueuesSelect,
  HookFormSelectionListSingle,
  HookFormText,
} from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';

type FieldValues = Omit<CreateWhatsAppSignupApiArg, 'queue_id'> & {
  queue_id: string[];
};

const formId = 'whatsapp-signup-form';

export const SettingsMessagingWhatsAppSetupView = (): JSX.Element => {
  const notify = useNotifications();
  const form = useForm<FieldValues>({ mode: 'onBlur' });
  const [countrySearchTerm, setCountrySearchTerm] = useState('');

  const [createWhatsAppSignup, { isSuccess }] =
    messagingApi.useCreateWhatsAppSignupMutation();

  const filteredCountries = useMemo(() => {
    const searchTerm = countrySearchTerm.trim();
    if (!searchTerm) {
      return countries;
    }

    const fuse = new Fuse(countries, {
      keys: ['name', 'countryCode'],
      threshold: 0.2,
      distance: 200,
    });
    return fuse.search(searchTerm).map(({ item }) => item);
  }, [countrySearchTerm]);

  const handleCountrySearchTermChange = useCallback((event) => {
    setCountrySearchTerm(event.target.value);
  }, []);

  const handleSubmit = async (values: FieldValues) => {
    try {
      await createWhatsAppSignup({
        ...values,
        queue_id: values.queue_id[0],
      }).unwrap();
    } catch {
      notify(
        {
          title: 'An error occurred while requesting WhatsApp access.',
        },
        NotificationType.Error,
      );
    }
  };

  const renderForm = () => (
    <form
      id={formId}
      noValidate
      tabIndex={-1}
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <Page.Section
        callout={
          <Banner
            variant="info"
            description={
              <Text.Paragraph as="span" variant="4">
                To request access for WhatsApp you need to fill in the fields
                below. Please note that WhatsApp need to approve your request
                and that can take up to 4 - 6 weeks.
              </Text.Paragraph>
            }
          />
        }
      />

      <Page.Section title="Company information">
        <FormGrid>
          <FormField span={3}>
            <HookFormText
              name="company_name"
              label="Company name"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'Company name...',
              }}
            />
          </FormField>

          <FormField span={3}>
            <HookFormText
              name="website"
              label="Company website"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'Company website...',
              }}
            />
          </FormField>

          <FormField>
            <HookFormText
              name="address"
              label="Address"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'Address...',
              }}
            />
          </FormField>

          <FormField span={3}>
            <HookFormText
              name="city"
              label="City"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'City...',
              }}
            />
          </FormField>

          <FormField span={3}>
            <HookFormText
              name="zip"
              label="Postal/Zip code"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'Postal/Zip code...',
              }}
            />
          </FormField>

          <FormField span={3}>
            <HookFormSelectionListSingle
              name="country"
              label="Country"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'Country...',
                getItemTitleByValue: (countryCode) =>
                  countries.find(
                    (country) => country.countryCode === countryCode,
                  )?.name ?? countryCode,
                filter: {
                  value: countrySearchTerm,
                  onChange: handleCountrySearchTermChange,
                },
                dropdownProps: {
                  style: {
                    maxHeight: 300,
                    overflow: 'auto',
                  },
                },
              }}
            >
              {filteredCountries.map((country) => (
                <SelectionList.Item
                  key={country.countryCode}
                  value={country.countryCode}
                >
                  {country.name}
                </SelectionList.Item>
              ))}
            </HookFormSelectionListSingle>
          </FormField>

          <FormField span={3}>
            <HookFormText
              name="state"
              label="State/Province"
              control={form.control}
              componentProps={{
                placeholder: 'State/Province...',
              }}
            />
          </FormField>
        </FormGrid>
      </Page.Section>

      <Page.Section title="Contact information">
        <FormGrid>
          <FormField span={3}>
            <HookFormText
              name="first_name"
              label="First name"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'First name...',
              }}
            />
          </FormField>

          <FormField span={3}>
            <HookFormText
              name="last_name"
              label="Last name"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'Last name...',
              }}
            />
          </FormField>

          <FormField span={3}>
            <HookFormText
              name="phone"
              label="Phone number"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
                validate: {
                  isValid: (phoneNumber) =>
                    phoneNumber
                      ? isNumberValidWithReason({ phoneNumber })
                      : true,
                },
              }}
              componentProps={{
                type: 'tel',
                placeholder: 'e.g. +1 205-832-1018',
              }}
            />
          </FormField>

          <FormField span={3}>
            <HookFormText
              name="email"
              label="Email"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
                pattern: {
                  value: /\S+@\S+\.\S+/,
                  message: 'Entered value does not match email format',
                },
              }}
              componentProps={{
                type: 'email',
                placeholder: 'e.g. john@example.com',
              }}
            />
          </FormField>
        </FormGrid>
      </Page.Section>

      <Page.Section title="WhatsApp information">
        <FormGrid>
          <FormField>
            <HookFormText
              name="facebook_manager_id"
              label="Facebook Business Manager ID"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'Facebook Business Manager ID...',
              }}
              helpIcon={{
                text: 'WhatsApp will not activate your account without this information. This number can be found in the "Business Info" section of your Facebook Business Manager Dashboard.',
                dark: true,
              }}
            />
          </FormField>

          <FormField>
            <HookFormText
              name="whatsapp_number"
              label="Phone number for WhatsApp Business account"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
                validate: {
                  isValid: (phoneNumber) =>
                    phoneNumber
                      ? isNumberValidWithReason({ phoneNumber })
                      : true,
                },
              }}
              componentProps={{
                type: 'tel',
                placeholder: 'e.g. +1 205-832-1018',
              }}
              helpIcon={{
                text: 'Please provide the number you wish to activate for your official WhatsApp Business Page. This can be any number you wish, provided it is not already in use on WhatsApp. Note, you must be able to receive a pin code via SMS or Voice on this number in order to complete activation.',
                dark: true,
              }}
            />
          </FormField>

          <FormField>
            <HookFormText
              name="whatsapp_display_name"
              label="WhatsApp display name"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              componentProps={{
                placeholder: 'WhatsApp display name...',
              }}
              helpIcon={{
                text: "This is the name that's displayed when your message appears in your customers's apps. It cannot be changed or edited later.",
                dark: true,
              }}
            />
          </FormField>

          <FormField>
            <HookFormQueuesSelect
              name="queue_id"
              label="Queue for incoming conversations"
              control={form.control}
              rules={{
                required: 'Please fill out this field',
              }}
              helpIcon={{
                text: 'All the WhatsApp conversations will be sent to the selected queue.',
                dark: true,
              }}
            />
          </FormField>

          <FormField span={2}>
            <Button
              type="submit"
              form={formId}
              disabled={!form.formState.isValid || form.formState.isSubmitting}
            >
              Request access
            </Button>
          </FormField>
        </FormGrid>
      </Page.Section>
    </form>
  );

  const renderSuccess = () => (
    <Page.Section>
      <FlexBox
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        gap="400"
      >
        <EmptyState
          title="Your request has been created!"
          description="We have received your request for setting up WhatsApp 👍. WhatsApp need
    to approve your request and that can take up to 4 - 6 weeks. But as
    soon as the approval goes through you will receive an email."
          icon="whatsapp"
        />

        <AppLink path="/settings/messaging">
          <Button variant="secondary">Back to overview</Button>
        </AppLink>
      </FlexBox>
    </Page.Section>
  );

  return (
    <Page>
      <Page.Head
        title={
          <FlexBox gap="200" alignItems="center">
            <AppLink path="/settings/messaging" style={{ color: 'inherit' }}>
              Third-party messaging
            </AppLink>
            <Icon name="angle-right" />
            Sign up to WhatsApp
          </FlexBox>
        }
      />

      <Page.Body>{isSuccess ? renderSuccess() : renderForm()}</Page.Body>
    </Page>
  );
};
